import { lazy } from "react";
import { useIntl } from "react-intl";
import { PermissionNames } from "~enums/permissions";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import type { TabRoute } from "../types/TabRoute";

const EntityGroupList = lazy(() => import("~Features/Club/ClubList"));
const CompetitionListPage = lazy(() => import("~Features/Competitions/CompetitionsList"));
const ConferenceListPage = lazy(() => import("~Features/Conferences/ConferencesList"));
const LeadersQualificationListPage = lazy(() => import("~Features/LeadersQualification/LeadersQualificationList"));
const LeagueListPage = lazy(() => import("~Features/Leagues/LeaguesList"));
const MatchProfileListPage = lazy(() => import("~Features/MatchProfiles/MatchProfilesList"));
const SiteListPage = lazy(() => import("~Features/Sites/SitesList"));
const StandingsConfigurationPage = lazy(() => import("~Features/StandingsConfigurations/StandingsConfigurationsList"));
const EntityListPage = lazy(() => import("~Features/Team/TeamsList"));
const VenueListPage = lazy(() => import("~Features/Venues/VenuesList"));
const PersonListPage = lazy(() => import("~pages/Persons/PersonList/PersonList"));
const DashboardPage = lazy(() => import("~pages/Dashboard/DashboardPage"));
const ConductListPage = lazy(() => import("~pages/Conducts/ConductsPage"));
const PermissionUserListPage = lazy(() => import("~pages/Permissions/PermissionUserListPage/PermissionUserListPage"));

const useGetOrganizationTabRoutes = (): TabRoute[] => {
  const { entityPermissionName } = usePermissionsByCompetitionName();
  const { formatMessage } = useIntl();

  return [
    {
      key: "dashboard",
      path: "dashboard",
      label: formatMessage({ id: "organization.tabs.dashboard" }),
      permissionKey: PermissionNames.ORGANIZATION,
      Component: DashboardPage,
    },
    {
      key: "leagues",
      path: "leagues",
      label: formatMessage({ id: "organization.tabs.leagues" }),
      permissionKey: PermissionNames.LEAGUE,
      Component: LeagueListPage,
    },
    {
      key: "competitions",
      path: "competitions",
      label: formatMessage({ id: "organization.tabs.competitions" }),
      permissionKey: PermissionNames.COMPETITION,
      Component: CompetitionListPage,
    },
    {
      key: "conferences",
      path: "conferences",
      label: formatMessage({ id: "organization.tabs.conferences" }),
      permissionKey: PermissionNames.CONFERENCE,
      Component: ConferenceListPage,
    },
    {
      key: "entity-groups",
      path: "entity-groups",
      label: formatMessage({ id: "organization.tabs.entityGroups" }),
      permissionKey: PermissionNames.ENTITY_GROUP,
      Component: EntityGroupList,
    },
    {
      key: "entities",
      path: "entities",
      label: formatMessage({ id: "organization.tabs.entities" }),
      permissionKey: entityPermissionName as PermissionNames,
      Component: EntityListPage,
    },
    {
      key: "sites",
      path: "sites",
      label: formatMessage({ id: "organization.tabs.sites" }),
      permissionKey: PermissionNames.SITE,
      Component: SiteListPage,
    },
    {
      key: "venues",
      path: "venues",
      label: formatMessage({ id: "organization.tabs.venues" }),
      permissionKey: PermissionNames.VENUE,
      Component: VenueListPage,
    },
    {
      key: "persons",
      path: "persons",
      label: formatMessage({ id: "organization.tabs.persons" }),
      permissionKey: PermissionNames.PERSON,
      Component: PersonListPage,
    },
    {
      key: "permissions",
      path: "permissions",
      label: formatMessage({ id: "organization.tabs.permissions" }),
      permissionKey: PermissionNames.PERMISSIONS,
      Component: PermissionUserListPage,
    },
    {
      key: "standings-configurations",
      path: "standings-configurations",
      label: formatMessage({ id: "organization.tabs.standings.configurations" }),
      permissionKey: PermissionNames.STANDING_CONFIGURATION,
      Component: StandingsConfigurationPage,
    },
    {
      key: "leaders-qualification",
      path: "leaders-qualification",
      label: formatMessage({ id: "organization.tabs.leaders.qualification" }),
      permissionKey: PermissionNames.LEADER_QUALIFIER,
      Component: LeadersQualificationListPage,
    },
    {
      key: "match-profiles",
      path: "match-profiles",
      label: formatMessage({ id: "organization.tabs.match.profiles" }),
      permissionKey: PermissionNames.MATCH_PROFILE,
      Component: MatchProfileListPage,
    },
    {
      key: "conducts",
      path: "conducts",
      label: formatMessage({ id: "organization.tabs.conduct" }),
      permissionKey: PermissionNames.CONDUCT,
      Component: ConductListPage,
    },
  ];
};

export default useGetOrganizationTabRoutes;
